import React, {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import {
  OwnerAssignmentButton,
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from 'src/components/Reusable/Buttons'
import { useCurrentUserEmail, useIsProvider } from 'src/context/authContext'
import { useTypewriter } from 'src/Utils/globalHooks'
import { QUESTIONNAIRE_ANSWER_TYPE } from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import Dropdown from 'react-bootstrap/esm/Dropdown'
import { RadioInput } from 'src/components/Reusable/Inputs/Radio'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { useSetActiveModal } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import dayjs, { Dayjs } from 'dayjs'
import { getHumanReadableDifference } from 'src/Utils/formatDate'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClockRotateLeft, faTurnUp } from '@fortawesome/free-solid-svg-icons'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { useNavigate } from 'react-router'
import {
  AiGrcQaAbsoluteRoutes,
  DirectRoutes,
} from 'src/components/Reusable/RootPage/RootPage.constants'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import {
  EroIcon,
  QuestionnaireP,
} from '../show/securityQuestionnaireShowPage.styles'
import { useGetQuestionnaire } from '../securityQuestionnaire.hooks'
import { isQuestionnaireScanning } from '../securityQuestionnaire.helpers'
import { SEC_QUESTIONNAIRE_POLL } from '../securityquestionnaire.constants'
import { P } from '../securityQuestionnaire.styles'
import {
  AskQuestionInputContainer,
  AskQuestionSuggestion,
  AskQuestionSuggestionsContainer,
  ChatHistoryContainer,
  QuestionHistoryContainer,
  QuestionnaireAnswerTypeDropdown,
  StyledAskQuestionButton,
  StyledAskQuestionInput,
  StyledCaption,
  StyledChooseAnswerTypeDropdownContainer,
  StyledSelectQcTypeButton,
} from './AskQuestion.styles'

export const TEMPLATE_SUGGESTIONS = [
  'Do employees undergo security awareness training? If so, how often do they receive training?',
  'How often is penetration testing conducted?',
  'Is there a user access review process?',
]

type AskQuestionInputProps = {
  value: string
  setValue: (val: string) => void
  onSubmit: ((v?: string) => void) | ((v?: string) => Promise<void>)
  submitText: string
  placeholder: string
  setPreferKb: () => void
  setPreferPlatform: () => void
  answerType: QUESTIONNAIRE_ANSWER_TYPE
  copilotQuestionHistory: string[]
  isMissingKb?: boolean
}

export const AskQuestionInput = ({
  value,
  setValue,
  onSubmit,
  submitText,
  placeholder,
  setPreferKb,
  setPreferPlatform,
  answerType,
  copilotQuestionHistory,
  isMissingKb,
}: AskQuestionInputProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const isProvider = useIsProvider()
  const isPreferPlatform =
    answerType === QUESTIONNAIRE_ANSWER_TYPE.BOTH_PREFER_PLATFORM
  const buyerMissingKb = !isProvider && isMissingKb
  const hasPermission = useCanPerformAction(CAPABILITY.CAN_ADD_QUESTIONNAIRES)

  const isDisabled = buyerMissingKb || !value.length

  const submitAndDismissFocus = (v?: string): void => {
    onSubmit(v)
    inputRef.current?.blur()
  }

  const handleGlobalKeyDown = (event: KeyboardEvent) => {
    if (
      (event.metaKey && event.key === 'k') ||
      (event.ctrlKey && event.key === 'k')
    ) {
      inputRef.current?.focus()
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [])

  const handleKeyPress: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'ArrowDown') {
      setHighlightedIndex((prevIndex) => {
        // If nothing is selected or last suggestion is selected, select the first one
        const nextIndex = prevIndex + 1
        return nextIndex < suggestions.length ? nextIndex : 0
      })
      event.preventDefault()
    } else if (event.key === 'ArrowUp') {
      setHighlightedIndex((prevIndex) => {
        // If nothing is selected or first suggestion is selected, select the last one
        const nextIndex = prevIndex - 1
        return nextIndex >= -1 ? nextIndex : suggestions.length - 1
      })
      event.preventDefault()
    } else if (event.key === 'Enter') {
      if (
        isInputFocused &&
        highlightedIndex >= -1 &&
        highlightedIndex < suggestions.length
      ) {
        onSubmit(suggestions[highlightedIndex]?.text)
        inputRef.current?.blur()
      } else {
        onSubmit() // This will execute the default submit behavior
      }
    } else if (event.key === 'Escape') {
      setHighlightedIndex(-1) // Reset the highlighted index
      inputRef.current?.blur()
    }
  }

  const historySuggestions = copilotQuestionHistory.map((text) => ({
    text,
    isHistory: true,
  }))
  const unfilteredSuggestions = [
    ...historySuggestions,
    ...TEMPLATE_SUGGESTIONS.map((text) => ({ text, isHistory: false })),
  ]
  const suggestionLookup = new Set<string>()
  const suggestions = unfilteredSuggestions.filter((s) => {
    if (suggestionLookup.has(s.text.trim().toLowerCase())) {
      return false
    }
    suggestionLookup.add(s.text.trim().toLowerCase())
    return true
  })
  const showSuggestions = isInputFocused && !!suggestions.length && !value

  const onFocus = () => {
    setHighlightedIndex(-1)
    setIsInputFocused(true)
  }
  const onBlur = () => {
    setHighlightedIndex(-1)
    setIsInputFocused(false)
  }

  if (!hasPermission) {
    return <></>
  }

  return (
    <AskQuestionInputContainer $expand={showSuggestions}>
      <StyledAskQuestionInput
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={2000}
        onKeyDown={handleKeyPress}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {showSuggestions && (
        <AskQuestionSuggestions
          suggestions={suggestions}
          askSuggestedQuestion={submitAndDismissFocus}
          highlightedIndex={highlightedIndex}
        />
      )}
      <FlexRow gap={5}>
        <ChooseAnswerTypeDropdown
          isPreferPlatform={isPreferPlatform}
          setPreferKb={setPreferKb}
          setPreferPlatform={setPreferPlatform}
          isPlg={!isProvider}
        />
        <StyledAskQuestionButton
          isDisabled={isDisabled}
          onClick={(e) => {
            e.preventDefault()
            submitAndDismissFocus()
          }}
          disabled={isDisabled}
        >
          {submitText}
        </StyledAskQuestionButton>
      </FlexRow>
    </AskQuestionInputContainer>
  )
}

export type SuggestionType = {
  text: string
  isHistory: boolean
}

export const AskQuestionSuggestions = ({
  suggestions,
  askSuggestedQuestion,
  highlightedIndex,
  isTI,
}: {
  suggestions: SuggestionType[]
  askSuggestedQuestion: (v?: string) => void | Promise<void>
  highlightedIndex: number
  isTI?: boolean
}): JSX.Element => {
  const handleSuggestionClick = (suggestion: string) => {
    askSuggestedQuestion(suggestion)
  }

  return (
    <AskQuestionSuggestionsContainer
      onMouseDown={(e) => e.preventDefault()}
      $isTI={isTI}
    >
      {suggestions.slice(0, 4).map(({ text, isHistory }, index) => (
        <AskQuestionSuggestion
          key={index}
          onClick={() => handleSuggestionClick(text)}
          $isHighlighted={highlightedIndex === index}
          $isTI={isTI}
        >
          <P>{text}</P>
          <FlexRow gap={15}>
            {isHistory && <FontAwesomeIcon icon={faClockRotateLeft} />}
            <FontAwesomeIcon icon={faTurnUp} />
          </FlexRow>
        </AskQuestionSuggestion>
      ))}
    </AskQuestionSuggestionsContainer>
  )
}

export const AskQuestionChatHistory = ({
  question,
  answer,
  setAnswer,
  questionnaireId,
  startTime,
}: {
  question: string
  answer: string
  questionnaireId: string
  setAnswer: (v: string) => void
  startTime: Dayjs
}): JSX.Element => {
  const [isScanning, setIsScanning] = useState(false)
  const [duration, setDuration] = useState<string>('')
  const navigate = useNavigate()

  const { data, mutate } = useGetQuestionnaire({
    id: questionnaireId,
    allowFetch: !!questionnaireId,
  })

  const intervalId = useRef<ReturnType<typeof setInterval> | null>(null)

  const initPolling = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current)
    }
    intervalId.current = setInterval(() => {
      mutate()
    }, SEC_QUESTIONNAIRE_POLL)
  }, [mutate])

  const clearIntervalFunc = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current)
      intervalId.current = null
    }
  }, [])

  useEffect(() => {
    if (!data) return

    if (isQuestionnaireScanning([data])) {
      setIsScanning(true)
      initPolling()
    } else {
      setIsScanning(false)
      clearIntervalFunc()
    }

    return () => clearIntervalFunc()
  }, [data, initPolling, clearIntervalFunc])

  useEffect(() => {
    if (data) {
      const question = data.getQuestionsList()[0]
      const newAnswer = question.getAnswer()
      const isQcScanning = isQuestionnaireScanning([data])
      if (isQcScanning && duration) {
        setDuration('')
      }

      if (!newAnswer && !isQcScanning) {
        setAnswer('No answer could be found.')
        setIsScanning(false)
        const endTime = dayjs()
        setDuration(getHumanReadableDifference(startTime, endTime))
      }
      if (newAnswer) {
        setAnswer(newAnswer)
        setIsScanning(false)
        const endTime = dayjs()
        setDuration(getHumanReadableDifference(startTime, endTime))
      }
    }
  }, [
    data,
    setAnswer,
    questionnaireId,
    isScanning,
    answer,
    startTime,
    duration,
  ])

  const goToQuestionnaire = () =>
    navigate(
      window.location.href.includes(DirectRoutes.DASHBOARD) ||
        window.location.href.includes(DirectRoutes.GRC_DASHBOARDS)
        ? `../${AiGrcQaAbsoluteRoutes.SHOW}/${questionnaireId}`
        : `../../${AiGrcQaAbsoluteRoutes.SHOW}/${questionnaireId}`,
    )

  return (
    <ChatHistoryContainer>
      <QuestionHistory text={question} />
      <AnswerHistory
        duration={duration}
        text={answer}
        isScanning={!data || isScanning}
      />
      {questionnaireId && (
        <FlexRow
          isFullWidth
          justify={FlexAlign.FLEX_START}
          $padding={10}
          $pl={35}
        >
          <TextButton onClick={goToQuestionnaire}>
            Go to AI GRC Q&A result
          </TextButton>
        </FlexRow>
      )}
    </ChatHistoryContainer>
  )
}

export const AnswerHistory = ({
  text,
  isScanning,
  duration,
}: {
  text: string
  isScanning: boolean
  duration: string
}): JSX.Element => {
  const ellipsis = useTypewriter({
    text: '...',
    interval: 500,
    shouldLoop: true,
  })

  const displayText = useTypewriter({
    text: text,
    interval: 30,
  })

  const displayDuration = 'Copilot answered in ' + duration

  return (
    <QuestionHistoryContainer>
      <FlexRow
        gap={8}
        justify={FlexAlign.FLEX_START}
        align={FlexAlign.FLEX_START}
      >
        <Tooltip
          id={text}
          tooltipBody={displayDuration}
          disabled={!duration}
          overlayType={TooltipOverlayType.popover}
          placement={TooltipPositions.top}
        >
          <div>
            <EroIcon $isSmall />
          </div>
        </Tooltip>
        <QuestionnaireP $fontSize={16} $isItalicized>
          {isScanning ? ellipsis : displayText}
        </QuestionnaireP>
      </FlexRow>
    </QuestionHistoryContainer>
  )
}

export const QuestionHistory = ({ text }: { text: string }): JSX.Element => {
  const email = useCurrentUserEmail()

  return (
    <QuestionHistoryContainer>
      <FlexRow
        gap={8}
        justify={FlexAlign.FLEX_START}
        align={FlexAlign.FLEX_START}
      >
        <OwnerAssignmentButton modelId={''} email={email} isDisabled />
        <QuestionnaireP $mt={4} $fontSize={16}>
          {text}
        </QuestionnaireP>
      </FlexRow>
    </QuestionHistoryContainer>
  )
}

type ChooseAnswerTypeProps = {
  isPreferPlatform: boolean
  setPreferKb: () => void
  setPreferPlatform: () => void
  isPlg?: boolean
}

const ChooseAnswerTypeDropdown = ({
  isPreferPlatform,
  setPreferKb,
  setPreferPlatform,
  isPlg,
}: ChooseAnswerTypeProps): JSX.Element => (
  <Dropdown autoClose="outside">
    <Dropdown.Toggle
      as={QuestionnaireAnswerTypeDropdown}
      id={`answer-type-dropdown`}
    >
      {isPlg ? (
        <p>
          {isPreferPlatform ? 'AI GRC Q&A - Advanced' : 'AI GRC Q&A - Basic'}
        </p>
      ) : (
        <p>
          {isPreferPlatform
            ? 'Prefer AI GRC Q&A - Advanced'
            : 'Prefer AI GRC Q&A - Basic'}
        </p>
      )}
    </Dropdown.Toggle>
    <Dropdown.Menu>
      <ChooseAnswerTypeDropdownItem
        isPreferPlatform={isPreferPlatform}
        setPreferKb={setPreferKb}
        setPreferPlatform={setPreferPlatform}
        isPlg={isPlg}
      />
    </Dropdown.Menu>
  </Dropdown>
)

const ChooseAnswerTypeDropdownItem = ({
  isPreferPlatform,
  setPreferKb,
  setPreferPlatform,
  isPlg,
}: ChooseAnswerTypeProps): JSX.Element => {
  const openModal = useSetActiveModal(ModalFormId.UPSELL_QC)
  const handleAdvanced = isPlg ? openModal : setPreferPlatform

  return (
    <StyledChooseAnswerTypeDropdownContainer>
      <StyledCaption>Answer type</StyledCaption>
      <div>
        <StyledSelectQcTypeButton onClick={setPreferKb}>
          <RadioInput
            id={`questionnaire-input-prefer-kb`}
            checked={!isPreferPlatform}
            value="PREFER_KB"
            onClick={setPreferKb}
          />
          <div>
            <P>{isPlg ? 'AI GRC Q&A - Basic' : 'Prefer AI GRC Q&A - Basic'}</P>
            <StyledCaption>
              {isPlg
                ? 'Answer questions using your uploaded knowledge base'
                : 'Uses your knowledge base Fastest answers, least expensive, not as accurate if knowledge base is out of date. Will try to answer with AI GRC Q&A - Advanced if no answer is found in the KB.'}
            </StyledCaption>
            <StyledCaption isDark>{'usually < 10 seconds'}</StyledCaption>
          </div>
        </StyledSelectQcTypeButton>
        <StyledSelectQcTypeButton onClick={handleAdvanced}>
          <RadioInput
            id={`questionnaire-input-prefer-platform`}
            checked={isPreferPlatform}
            value="PREFER_PLATFORM"
            onClick={handleAdvanced}
          />
          <div>
            <P>
              {isPlg ? 'AI GRC Q&A - Advanced' : 'Prefer AI GRC Q&A - Advanced'}
            </P>
            <StyledCaption>
              {isPlg
                ? 'Powered by your GRC content for the most accurate and up to date answers'
                : 'Uses your GRC content. Most thorough, up-to-date and accurate answers. Will try to answer with AI GRC Q&A - Basic if no answer is found in the GRC Platform.'}
            </StyledCaption>
            <StyledCaption isDark>{'usually < 1 minute'}</StyledCaption>
            {isPlg && (
              <StandardButton
                type="button"
                buttonSize={StandardButtonSize.SMALL}
                variant={StandardButtonVariant.PRIMARY}
                marginTop={8}
                onClick={handleAdvanced}
                width={'100%'}
              >
                Click here to upgrade
              </StandardButton>
            )}
          </div>
        </StyledSelectQcTypeButton>
      </div>
    </StyledChooseAnswerTypeDropdownContainer>
  )
}
